/**
 * English: Sidebar to pick Manager, Master, or a specific device agent for chat routing.
 * Thai: แถบข้างเลือก Manager, Master หรือ device agent สำหรับเส้นทางแชท
 */
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Agent, AgentsMap, AgentLiveStatus, Station } from '../../types';

interface TalkToPanelProps {
  chatTarget: string;
  onSelect: (target: string) => void;
  showMaster?: boolean;
  agentStatuses: Record<string, AgentLiveStatus>;
  agents: AgentsMap;
  stations: Station[];
  activeStationId: string;
  onStationChange: (id: string) => void;
}

function StatusDot({ status }: { status: AgentLiveStatus }) {
  if (status === 'speaking') {
    return (
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
      </span>
    );
  }
  if (status === 'completed') {
    return <CheckCircleIcon className="w-3 h-3 text-app-accent shrink-0" />;
  }
  return <span className="h-2 w-2 rounded-full bg-gray-500/30 shrink-0" />;
}

function TargetRow({
  id,
  name,
  role,
  color,
  status,
  active,
  onClick,
}: {
  id: string;
  name: string;
  role?: string;
  color: string;
  status: AgentLiveStatus;
  active: boolean;
  onClick: () => void;
}) {
  const initials = name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-left group ${
        active
          ? 'bg-app-accent/10 ring-1 ring-app-accent/30'
          : 'hover:bg-app-muted'
      } ${status === 'speaking' ? 'bg-green-500/8' : ''}`}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0 transition-transform group-hover:scale-105"
        style={{ backgroundColor: color }}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-xs font-medium truncate ${active ? 'text-app-accent' : 'text-app-text'}`}>
          {name}
        </div>
        {role && <div className="text-[10px] text-app-text-dim truncate">{role}</div>}
      </div>
      <StatusDot status={status} />
    </button>
  );
}

export default function TalkToPanel({
  chatTarget,
  onSelect,
  showMaster = true,
  agentStatuses,
  agents,
  stations,
  activeStationId,
  onStationChange,
}: TalkToPanelProps) {
  const manager = agents.manager as Agent | undefined;
  const deviceAgents = Object.entries(agents).filter(([id]) => id !== 'manager');

  return (
    <div className="flex flex-col border-r border-app-border bg-app-surface shrink-0 w-52 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-app-border">
        <span className="text-[10px] font-semibold text-app-text-dim uppercase tracking-wider">Talk to</span>
      </div>

      <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-1">
        {/* Master */}
        {showMaster && (
          <TargetRow
            id="master"
            name="Master"
            role="Cross-station"
            color="#a855f7"
            status={agentStatuses.master || 'waiting'}
            active={chatTarget === 'master'}
            onClick={() => onSelect('master')}
          />
        )}

        {/* Divider + Station */}
        <div className="px-2 pt-3 pb-1 flex items-center gap-2">
          <span className="text-[10px] font-semibold text-app-text-dim uppercase tracking-wider">Station</span>
          {stations.length > 1 ? (
            <select
              value={activeStationId}
              onChange={(e) => onStationChange(e.target.value)}
              className="flex-1 min-w-0 bg-app-bg border border-app-border rounded px-1.5 py-0.5 text-[10px] text-app-text outline-none focus:ring-1 focus:ring-app-accent"
            >
              {stations.map((s) => (
                <option key={s.id} value={s.id}>{s.name || s.id}</option>
              ))}
            </select>
          ) : (
            <span className="text-[10px] text-app-text-sec truncate">
              {stations.find((s) => s.id === activeStationId)?.name || activeStationId}
            </span>
          )}
        </div>

        {/* Manager */}
        {manager && (
          <>
            <TargetRow
              id="manager"
              name={manager.name || 'Manager'}
              role="Station manager"
              color={manager.color || '#2563eb'}
              status={agentStatuses.manager || 'waiting'}
              active={chatTarget === 'manager'}
              onClick={() => onSelect('manager')}
            />
            <p className="px-2 pb-1 text-[9px] leading-snug text-app-text-dim">
              Ask/Control + Manager → station team graph · ถาม/สั่งที่ Manager = กราฟทีมสถานีนี้
            </p>
          </>
        )}

        {/* Device Agents */}
        {deviceAgents.length > 0 && (
          <>
            <div className="px-2 pt-3 pb-1">
              <span className="text-[10px] font-semibold text-app-text-dim uppercase tracking-wider">
                Devices ({deviceAgents.length})
              </span>
              <p className="mt-0.5 text-[9px] leading-snug text-app-text-dim">
                MQTT device agents — not HR staff · agent ของอุปกรณ์ ไม่ใช่พนักงาน HR
              </p>
            </div>
            {deviceAgents.map(([id, agent]) => {
              const a = agent as Agent;
              return (
                <TargetRow
                  key={id}
                  id={id}
                  name={a.name || id}
                  role={a.role || (typeof a.type === 'string' ? a.type : undefined)}
                  color={a.color || '#64748b'}
                  status={agentStatuses[id] || 'waiting'}
                  active={chatTarget === id}
                  onClick={() => onSelect(id)}
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
